import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocalizationStore } from '@/stores/localizationStore';
import { useAuthStore } from '@/stores/authStore';
import { getStringBundle } from '@/services/localizationService';

/**
 * Translation hook — returns a `t()` function that resolves localisation keys.
 *
 * The string bundle is fetched from the localisation service and cached.
 * Changing the language in the store triggers a new fetch.
 *
 * @example
 * const { t } = useTranslation();
 * return <h1>{t('K_TENANTS')}</h1>;
 */
export function useTranslation() {
  const language = useLocalizationStore((s) => s.language);
  const organizationId = useAuthStore((s) => s.session?.organization.id) ?? undefined;

  const { data: strings, isLoading } = useQuery({
    queryKey: ['localization', 'bundle', language, organizationId ?? ''],
    queryFn: () => getStringBundle(language, organizationId),
    staleTime: Infinity,
    gcTime: 30 * 60 * 1000,
  });

  const t = useCallback(
    (key: string, fallback?: string): string => {
      return strings?.[key] ?? fallback ?? key;
    },
    [strings],
  );

  return { t, isLoading };
}
